import React, { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { useForm } from "react-hook-form";
import { SubmitHandler } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";

import { useGetLocationData } from "~/hooks/locationData";
import { useGetAllGroupsForSelect } from "~/hooks/Selectors/useGetAllGroupsForSelect";
import { optionsStatus } from "~/pages/Editor/EditorCatalogEducationalPrograms/options";
import {
  CurriculumManagementProps,
  ILevel,
  SelectOptions,
} from "~/pages/Editor/EditorCatalogEducationalPrograms/types";
import {
  transformStatus,
  useGetLevel,
} from "~/pages/Editor/EditorCatalogEducationalPrograms/utils";
import { OpenModal } from "~/pages/Editor/type";
import {
  useCreateCurriculumLevelMutation,
  useEditCurriculumLevelMutation,
} from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";
import { addGMTOffsetToDate } from "~/shared/lib/addGMTOffsetToDate";
import { FormStatus } from "~/shared/lib/statuses";
import { Field } from "~/shared/ui/Field";

import { schema } from "./options";

import s from "~/pages/Editor/EditorCatalogEducationalPrograms/ManagementForm.less";

export type FormSubmit = {
  name: string;
  status: SelectOptions;
  group: SelectOptions;
  sequenceNumber: number;
  isCertification: boolean;
};

const defaultValues = {
  name: "",
  status: { label: "", value: "" },
  group: { label: "", value: "" },
  sequenceNumber: null,
  isCertification: false,
};

export const LevelsManagement = ({
  formId,
  refetch,
  closeModal,
}: CurriculumManagementProps) => {
  const [currentLevel, setCurrentLevel] = useState<ILevel>(null);

  const { t } = useTranslation();
  const { notifySuccess, notifyError } = useNotify();
  const { openModal, currentId } = useGetLocationData();

  const currentDate = `${new Date()}`;
  const { level, refetch: refetchLevel } = useGetLevel(currentId);

  const { groups, searchText, intersectionObserver } =
    useGetAllGroupsForSelect();

  const { handleSubmit, control, setValue, errors } = useForm<FormSubmit>({
    defaultValues,
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    openModal === OpenModal.Edit ? refetchLevel() : null;
  }, [openModal]);

  const [editLevel] = useEditCurriculumLevelMutation({
    onCompleted() {
      refetch({
        ActualOn: addGMTOffsetToDate(currentDate),
      });
      closeModal();
      notifySuccess(t("common.сhange_was_successful"));
    },
    onError(vals) {
      notifyError(vals["message"]);
    },
  });

  const [createLevel] = useCreateCurriculumLevelMutation({
    onCompleted() {
      refetch({
        ActualOn: addGMTOffsetToDate(currentDate),
      });
      closeModal();
      notifySuccess(t("common.сhange_was_successful"));
    },
    onError(vals) {
      notifyError(vals["message"]);
    },
  });

  const submit: SubmitHandler<FormSubmit> = (data) => {
    const { name, status, sequenceNumber, group } = data;

    if (openModal === OpenModal.Edit) {
      editLevel({
        variables: {
          id: currentId,
          name,
          status: FormStatus[status.value],
          GroupId: group.value,
          sequenceNumber: Number(sequenceNumber),
        },
      });
    } else if (openModal === OpenModal.Add) {
      createLevel({
        variables: {
          name,
          status: FormStatus[status.value],
          GroupId: group.value,
          sequenceNumber: Number(sequenceNumber),
        },
      });
    }
  };

  useEffect(() => {
    if (currentLevel && openModal === OpenModal.Edit && currentLevel.Group) {
      setValue("name", currentLevel.name);
      setValue("status", {
        label: transformStatus(currentLevel.status),
        value: currentLevel.status,
      });
      setValue("group", {
        label: currentLevel.Group.name,
        value: currentLevel.Group.id,
      });
      setValue("sequenceNumber", currentLevel.sequenceNumber);
    }
  }, [currentLevel, openModal]);

  useEffect(() => {
    level && setCurrentLevel(level as ILevel);
  }, [level]);

  const handlePopupScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
    const target = e.currentTarget;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 16) {
      intersectionObserver();
    }
  };

  return (
    <form id={formId} className={s.form} onSubmit={handleSubmit(submit)}>
      <div className={s.items}>
        <Field
          type="input"
          name="name"
          control={control}
          label={t("cep.name")}
          error={errors["name"]?.message}
        />
        <Field
          type="select"
          name="status"
          control={control}
          label={t("cep.status")}
          options={optionsStatus}
          labelInValue
          error={errors["status"]?.value?.message}
        />
        <Field
          type="select"
          name="group"
          control={control}
          label={t("cep.groups")}
          options={groups || []}
          labelInValue
          showSearch
          filterOption={false}
          onSearch={searchText}
          onPopupScroll={handlePopupScroll}
          error={errors["group"]?.value?.message}
        />
        <Field
          type="input"
          name="sequenceNumber"
          control={control}
          label={t("cep.sequenceNumber")}
          error={errors["sequenceNumber"]?.message}
        />
      </div>
    </form>
  );
};
